// A product's prices and Price Grids on the quote (migrations 0070, 0071):
// which price a line can move to, which grid options it can take, and what
// the catalog shows for a product. Pure: the workspace passes what it loaded.
import type { FilterOption } from '@olytic/foundation';

import type { BuilderLine, BuilderQuote, CatalogProduct, CompanySize, PriceGrid, PriceOption } from './quotesApi';

/** A product's current prices, the default first. */
export function pricesOf(productId: string | null, options: PriceOption[]): PriceOption[] {
  if (!productId) return [];
  return options.filter((o) => o.product_id === productId).sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.label.localeCompare(b.label));
}

/** The Company Size the quote prices at: its own choice, else its account's. */
export function sizeKeyOf(quote: Pick<BuilderQuote, 'grid_overrides' | 'company_size'>): string | null {
  return quote.grid_overrides?.['account.company_size'] ?? quote.company_size ?? null;
}

/** A dimension value's label: a Company Size's, or the value the grid lists. */
function valueLabel(grid: PriceGrid, index: number, key: string, sizes: CompanySize[]): string {
  const dimension = grid.dimensions[index];
  if (dimension?.object === 'account' && dimension.field === 'company_size') return sizes.find((s) => s.key === key)?.label ?? key;
  return dimension?.values?.find((v) => v.key === key)?.label ?? key;
}

/** What a cell is called: its own label, else its values ("2 Workstreams · Mid-Market"). */
export function cellLabel(grid: PriceGrid, cell: PriceGrid['cells'][number], sizes: CompanySize[]): string {
  if (cell.label) return cell.label;
  const parts = [valueLabel(grid, 0, cell.key_1, sizes)];
  if (cell.key_2 !== null) parts.push(valueLabel(grid, 1, cell.key_2, sizes));
  return parts.join(' · ');
}

/** The grid a line's price uses, if it is a grid price. */
export function gridOf(line: Pick<BuilderLine, 'price_scheme' | 'price_grid_cell_snapshot' | 'price_id'>, options: PriceOption[], grids: PriceGrid[]): PriceGrid | null {
  if (line.price_scheme !== 'grid') return null;
  const gridId = line.price_grid_cell_snapshot?.grid_id ?? options.find((o) => o.id === line.price_id)?.price_grid_id ?? null;
  return grids.find((g) => g.id === gridId) ?? null;
}

/** A grid line's options, as a menu: each cell's label and amount. */
export function cellOptions(grid: PriceGrid | null, sizes: CompanySize[], money: (n: number) => string): FilterOption[] {
  if (!grid) return [];
  return grid.cells.map((cell) => ({ value: cell.id, label: `${cellLabel(grid, cell, sizes)} · ${money(cell.amount)}` }));
}

/** A line's price choices: each of its product's prices, by label (and amount for a flat price). */
export function priceChoices(productId: string | null, options: PriceOption[], money: (n: number) => string): FilterOption[] {
  return pricesOf(productId, options).map((o) => ({
    value: o.id,
    label: o.scheme === 'grid' ? `${o.label} · Price Grid` : o.scheme === 'included' ? `${o.label} · Included` : `${o.label} · ${money(o.amount)}`,
  }));
}

/**
 * What the catalog shows for a product: its default price's amount, or for a
 * grid price the cell for the quote's Company Size (a one-dimension grid);
 * null when that needs a size or more than one value.
 */
export function catalogPrice(product: CatalogProduct, ctx: { options: PriceOption[]; grids: PriceGrid[]; sizeKey: string | null }): number | null {
  const price = pricesOf(product.id, ctx.options)[0];
  if (!price) return null;
  if (price.scheme !== 'grid') return price.amount;
  const grid = ctx.grids.find((g) => g.id === price.price_grid_id);
  if (!grid || grid.dimensions.length !== 1 || ctx.sizeKey === null) return null;
  return grid.cells.find((c) => c.key_1 === ctx.sizeKey)?.amount ?? null;
}

/** A line's quantity with what it counts, when that is not plain units ("12 Hours"). */
export function quantityText(line: Pick<BuilderLine, 'quantity' | 'quantity_label'>): string {
  return line.quantity_label && line.quantity_label !== 'Quantity' ? `${line.quantity} ${line.quantity_label}` : String(line.quantity);
}
